import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Search, X } from "lucide-react";
import { QueryDocumentSnapshot } from "firebase/firestore";
import WorkspaceNavigation, { Brand } from "./WorkspaceNavigation";
import SettingsButton from "./SettingsButton";
import SlidingIndicator from "./SlidingIndicator";
import FeedPost from "./FeedPost";
import FeedModeration from "./FeedModeration";
import { useAuth } from "../contexts/AuthContext";
import { hasFreeAccess } from "../utils/freeAccess";
import { useNow } from "../hooks/useNow";
import {
  FeedPost as FeedPostData,
  FeedSort,
  loadFeedPage,
  loadFeedPost,
  searchFeed,
} from "../firebase/feed";

const SORTS: { value: FeedSort; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "top", label: "Most liked" },
  { value: "discussed", label: "Most discussed" },
];

const SEARCH_DEBOUNCE_MS = 250;

function useRevealed() {
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const reveal = useCallback((id: string) => setRevealed((s) => new Set(s).add(id)), []);
  return [revealed, reveal] as const;
}

function SinglePost({ id, now }: { id: string; now: number }) {
  const [post, setPost] = useState<FeedPostData | null | undefined>(undefined);
  const [revealed, reveal] = useRevealed();
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setPost(undefined);
    loadFeedPost(id).then(setPost).catch(() => setPost(null));
  }, [id]);

  return (
    <>
      <Link
        to="/feed"
        className="inline-flex items-center gap-1.5 -ml-2 px-2 py-1.5 rounded-md text-sm text-muted hover:bg-subtle hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-150"
      >
        <ArrowLeft size={15} aria-hidden="true" />
        All debates
      </Link>
      {post === undefined && <p className="mt-6 text-sm text-muted" role="status">Loading debate…</p>}
      {post === null && (
        <div className="mt-6">
          <h1 className="text-lg font-semibold tracking-tight text-content">This debate isn't available</h1>
          <p className="mt-1 text-sm text-muted">Its author removed it, or it was hidden after reports.</p>
        </div>
      )}
      {post && (
        <FeedPost
          post={post}
          onRemoved={() => setPost(null)}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          revealed={revealed.has(post.id)}
          onReveal={() => reveal(post.id)}
          now={now}
        />
      )}
    </>
  );
}

function FeedList({ now, isModerator }: { now: number; isModerator: boolean }) {
  const [sort, setSort] = useState<FeedSort>("newest");
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [posts, setPosts] = useState<FeedPostData[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [revealed, reveal] = useRevealed();
  const sentinel = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  const searchInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchTerm(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const id = ++requestId.current;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setExpandedId(null);
    const request = searchTerm
      ? searchFeed(searchTerm, controller.signal).then((found) => ({ posts: found, cursor: null }))
      : loadFeedPage(sort);
    request
      .then((page) => {
        if (id !== requestId.current) return;
        setPosts(page.posts);
        setCursor(page.cursor);
      })
      .catch((err) => {
        if (id !== requestId.current || err?.name === "AbortError") return;
        setPosts([]);
        setCursor(null);
        setError(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    return () => controller.abort();
  }, [sort, searchTerm, reloadKey]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore || searchTerm) return;
    const id = requestId.current;
    setLoadingMore(true);
    try {
      const page = await loadFeedPage(sort, cursor);
      if (id !== requestId.current) return;
      setPosts((p) => [...p, ...page.posts.filter((n) => !p.some((o) => o.id === n.id))]);
      setCursor(page.cursor);
    } catch {
      if (id === requestId.current) setError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, searchTerm, sort]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !cursor || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), { rootMargin: "600px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cursor, loadMore]);

  const searching = searchTerm.length > 0;

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold tracking-tight text-content">Public debates</h1>
          {isModerator && (
            <Link to="/feed/moderation" className="text-sm text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm">
              Reports
            </Link>
          )}
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={15} aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            ref={searchInput}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            placeholder="Search debate topics"
            aria-label="Search debate topics"
            maxLength={200}
            className="w-full h-9 pl-8 pr-8 rounded-md border border-line bg-surface text-sm text-content placeholder:text-muted hover:border-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-focus [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                searchInput.current?.focus();
              }}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted hover:bg-subtle hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 pb-3 border-b border-line">
        {searching ? (
          <p className="text-sm text-muted" role="status">
            {loading ? "Searching…" : (
              <>
                <span className="font-mono tabular-nums">{posts.length}</span> {posts.length === 1 ? "match" : "matches"}, best match first
              </>
            )}
          </p>
        ) : (
          <div className="relative flex items-center gap-0.5 p-0.5 rounded-md border border-line bg-raised" role="group" aria-label="Sort debates">
            <SlidingIndicator selector='[aria-pressed="true"]' activeKey={sort} className="seg-indicator-paper" />
            {SORTS.map((s) => (
              <button
                key={s.value}
                type="button"
                aria-pressed={sort === s.value}
                onClick={() => setSort(s.value)}
                className={`relative z-[1] px-2.5 py-1 rounded-md text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus active:translate-y-px ${
                  sort === s.value ? "text-content font-medium" : "text-muted hover:text-content"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && !searching && (
        <div role="status" aria-label="Loading debates">
          {[78, 64, 71].map((w, i) => (
            <div key={i} className="py-5 border-b border-line" aria-hidden="true">
              <span className="skeleton block h-2.5 w-40" />
              <span className="skeleton block h-4 mt-3" style={{ width: `${w}%` }} />
              <span className="skeleton block h-3 mt-2.5 w-11/12" />
              <span className="skeleton block h-3 mt-1.5 w-2/3" />
            </div>
          ))}
        </div>
      )}
      {!loading && error && posts.length === 0 && (
        <div className="py-10">
          <p className="text-sm text-content">Couldn't load debates.</p>
          <button type="button" className="mt-2 text-sm font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm" onClick={() => setReloadKey((k) => k + 1)}>
            Try again
          </button>
        </div>
      )}
      {!loading && !error && posts.length === 0 && (
        <div className="py-10">
          {searching ? (
            <p className="text-sm text-muted">No debates match “{searchTerm}”. Try fewer or different words.</p>
          ) : (
            <>
              <p className="text-sm text-content">No public debates yet.</p>
              <p className="mt-1 text-sm text-muted">
                Turn on “Post to the public feed” before you launch a debate, or post a finished one from its results screen.
              </p>
            </>
          )}
        </div>
      )}

      {(!loading || searching) && posts.map((post) => (
        <FeedPost
          key={post.id}
          post={post}
          expanded={expandedId === post.id}
          onToggle={() => setExpandedId((id) => (id === post.id ? null : post.id))}
          onRemoved={() => setPosts((list) => list.filter((p) => p.id !== post.id))}
          revealed={revealed.has(post.id)}
          onReveal={() => reveal(post.id)}
          now={now}
        />
      ))}

      {cursor && !searching && (
        <div ref={sentinel} className="py-6 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-line bg-surface text-sm font-medium text-content hover:bg-subtle active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {loadingMore && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
}

export default function FeedView({ moderation = false }: { moderation?: boolean }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isModerator = hasFreeAccess(currentUser?.email);
  const { postId } = useParams();
  const now = useNow(60_000);

  return (
    <div className="app-frame is-page-scroll">
      <a href="#feed-workspace" className="skip-link">Skip to feed</a>
      <header className="workspace-header">
        <Brand />
        <WorkspaceNavigation
          active="feed"
          onChange={(mode) => {
            if (mode === "video") navigate("/video");
            else if (mode === "chat") navigate("/?mode=chat");
            else if (mode === "council") navigate("/");
            else if (postId || moderation) navigate("/feed");
          }}
        />
        <div className="header-actions"><SettingsButton /></div>
      </header>
      <main id="feed-workspace" className="flex-1 overflow-x-clip">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 pt-4 pb-16">
          {moderation ? (
            isModerator ? <FeedModeration /> : <p className="text-sm text-muted">Only moderators can see this page.</p>
          ) : postId ? (
            <SinglePost id={postId} now={now} />
          ) : (
            <FeedList now={now} isModerator={isModerator} />
          )}
        </div>
      </main>
    </div>
  );
}
